<template>
  <div class="delete-request-review">
    <div class="lvyun-profile-banner">
      <h2>审核申请</h2>
      <p>集中审核订单、兑换券、积分商品等删除申请。</p>
    </div>

    <el-card class="review-card" shadow="never">
      <template slot="header">
        <div class="card-header">
          <div class="card-title">审核申请（管理员/超级管理员）</div>
          <el-button size="small" class="btn-secondary" :loading="loading" @click="loadDeleteRequests">
            刷新
          </el-button>
        </div>
      </template>

      <el-table
        :data="deleteRequests"
        style="width: 100%"
        stripe
        class="review-table"
        :row-class-name="deleteRequestRowClassName"
      >
        <el-table-column prop="id" label="申请ID" width="90" />
        <el-table-column label="目标" min-width="170">
          <template slot-scope="scope">
            <div>{{ targetTypeLabel(scope.row.target_type) }}</div>
            <div class="delete-request-subtext">{{ scope.row.target_label || scope.row.target_id }}</div>
          </template>
        </el-table-column>
        <el-table-column label="申请人" min-width="150">
          <template slot-scope="scope">
            <div>{{ scope.row.requested_by_name || '—' }}</div>
            <div class="delete-request-subtext">{{ scope.row.requested_by_role || '—' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="申请时间" width="180" />
        <el-table-column label="状态" width="120">
          <template slot-scope="scope">
            <el-tag size="mini" :type="deleteRequestStatusType(scope.row.status)">
              {{ deleteRequestStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180">
          <template slot-scope="scope">
            <el-button
              v-if="String(scope.row.status) === 'pending'"
              size="small"
              type="success"
              plain
              @click="reviewDeleteRequest(scope.row, 'approve')"
            >
              通过并删除
            </el-button>
            <el-button
              v-if="String(scope.row.status) === 'pending'"
              size="small"
              type="danger"
              plain
              @click="reviewDeleteRequest(scope.row, 'reject')"
            >
              驳回
            </el-button>
            <span v-if="String(scope.row.status) !== 'pending'" class="delete-request-subtext">已处理</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getUserInfo } from '../utils/auth'

export default {
  name: 'DeleteRequestReview',
  data() {
    return {
      deleteRequests: [],
      loading: false,
      focusedDeleteRequestId: null
    }
  },
  computed: {
    canReviewDeleteRequestsSession() {
      const u = getUserInfo()
      const r = String((u && u.roleLabel) || '').trim()
      return r === '管理员' || r === '超级管理员'
    }
  },
  mounted() {
    const qid = this.$route && this.$route.query ? this.$route.query.deleteRequestId : ''
    this.focusedDeleteRequestId = qid ? Number(qid) : null
    this.loadDeleteRequests({ allStatuses: !!qid })
  },
  watch: {
    '$route.query.deleteRequestId': {
      immediate: false,
      handler(v) {
        this.focusedDeleteRequestId = v ? Number(v) : null
        this.loadDeleteRequests({ allStatuses: !!v })
      }
    }
  },
  methods: {
    async loadDeleteRequests(opts = {}) {
      if (!this.canReviewDeleteRequestsSession) {
        this.$message.error('仅管理员或超级管理员可查看删除申请')
        return
      }
      this.loading = true
      try {
        const params = { status: opts.allStatuses ? 'all' : 'pending' }
        const resp = await this.$api.get('/admin/delete-requests', { params })
        if (resp && resp.code === 200 && Array.isArray(resp.data)) {
          this.deleteRequests = resp.data
          if (this.focusedDeleteRequestId) {
            const hit = this.deleteRequests.some((x) => Number(x.id) === Number(this.focusedDeleteRequestId))
            this.$nextTick(() => {
              if (hit) this.$message.success(`已定位到删除申请 #${this.focusedDeleteRequestId}`)
              else this.$message.info(`未找到删除申请 #${this.focusedDeleteRequestId}`)
            })
          }
        } else {
          this.deleteRequests = []
        }
      } catch (err) {
        this.deleteRequests = []
        const msg =
          (err && err.response && err.response.data && err.response.data.message) || '删除申请加载失败'
        this.$message.error(msg)
      } finally {
        this.loading = false
      }
    },
    targetTypeLabel(t) {
      const x = String(t || '').trim()
      if (x === 'order') return '订单删除'
      if (x === 'voucher') return '兑换券删除'
      if (x === 'points_goods') return '积分商品删除'
      return x || '未知类型'
    },
    deleteRequestStatusText(s) {
      const x = String(s || '').trim()
      if (x === 'pending') return '待审核'
      if (x === 'approved') return '已通过'
      if (x === 'rejected') return '已驳回'
      return x || '未知'
    },
    deleteRequestStatusType(s) {
      const x = String(s || '').trim()
      if (x === 'pending') return 'warning'
      if (x === 'approved') return 'success'
      if (x === 'rejected') return 'info'
      return ''
    },
    deleteRequestRowClassName({ row }) {
      if (this.focusedDeleteRequestId && Number(row.id) === Number(this.focusedDeleteRequestId)) {
        return 'delete-request-row-focus'
      }
      return ''
    },
    async reviewDeleteRequest(row, action) {
      const id = row && row.id
      if (!id) return
      const isApprove = action === 'approve'
      try {
        await this.$confirm(
          isApprove ? '确认通过该删除申请并立即执行删除？' : '确认驳回该删除申请？',
          isApprove ? '通过删除申请' : '驳回删除申请',
          { type: isApprove ? 'warning' : 'info' }
        )
      } catch (_e) {
        return
      }
      try {
        const resp = await this.$api.put(`/admin/delete-requests/${id}/review`, { action, note: '' })
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '处理成功')
          await this.loadDeleteRequests({ allStatuses: true })
        } else {
          this.$message.error((resp && resp.message) || '处理失败')
        }
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.message) || '处理失败'
        this.$message.error(msg)
      }
    }
  }
}
</script>

<style>
.delete-request-review {
  padding: 24px;
  background: #f5f7fa;
}

.review-card {
  border-radius: 12px;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.06);
  border: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}

.btn-secondary {
  border-radius: 8px;
  background: #f3f4f6;
  border-color: #e5e6eb;
  color: #4e5969;
}

.delete-request-subtext {
  color: #86909c;
  font-size: 12px;
  margin-top: 2px;
}

.review-table ::v-deep(.delete-request-row-focus > td) {
  background: #ecf5ff !important;
}
</style>
